import type {Thread} from '@github-ui/diff-lines/conversation-types'
import type {ConversationComment, ConversationIssueComment} from '../../../page-data/payloads/conversation-comments'
import {assignThreadsToReviews, type ReviewWithThreads} from './assign-threads-to-reviews'

/**
 * A tagged wrapper that identifies an item in the merged conversation list
 * as either an issue comment or a review summary, preserving the original type
 * for rendering with the appropriate component.
 */
export type ConversationItem =
  | {type: 'issue-comment'; data: ConversationIssueComment; sortDate: string; key: string}
  | {type: 'review-summary'; data: ReviewWithThreads; sortDate: string; key: string}
  | {type: 'thread'; data: Thread; sortDate: string; key: string}

/**
 * Builds unsorted list of ConversationItems from issue comments and review
 * summaries for display in the Conversation tab.
 */
export function buildConversationItems(
  conversationComments: readonly ConversationComment[],
  threads: readonly Thread[],
): ConversationItem[] {
  const reviewSummaries = conversationComments.filter(c => c.type === 'review_summary')
  const issueComments = conversationComments.filter(c => c.type === 'issue')

  const {reviews: reviewsWithThreads, unassignedThreads} = assignThreadsToReviews(reviewSummaries, threads)

  return [
    ...issueComments.map(
      (comment): ConversationItem => ({
        type: 'issue-comment',
        data: comment,
        sortDate: comment.createdAt,
        key: `issue-comment-${comment.id}`,
      }),
    ),
    ...reviewsWithThreads
      .filter(review => !isEmptyCommentedReview(review))
      .map(
        (review): ConversationItem => ({
          type: 'review-summary',
          data: review,
          sortDate: review.summary.createdAt,
          key: `review-summary-${review.summary.id}`,
        }),
      ),
    ...unassignedThreads.map(
      (thread): ConversationItem => ({
        type: 'thread',
        data: thread,
        // An empty thread shouldn't be possible, but we have to provide a default to satisfy the typechecker
        sortDate: thread.commentsData?.comments?.[0]?.createdAt ?? '0000-01-01T00:00:00Z',
        key: `thread-${thread.id}`,
      }),
    ),
  ]
}

/**
 * Checks if a review is completely empty and opinionless. This can happen when all threads
 * are deleted after the review is submitted.
 */
function isEmptyCommentedReview(review: ReviewWithThreads): boolean {
  return review.summary.state === 'COMMENTED' && review.threads.length === 0 && !review.summary.body?.trim()
}
